import { useState, useEffect } from 'react';
import {
  Bell,
  Mail,
  Monitor,
  Save,
  Loader2,
  Smartphone,
} from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { useNotifications } from '@/hooks/useNotifications';
import {
  ChannelPreferences,
} from '@/types/notification.types';
import { oneSignalService } from '@/services/onesignal.service';
import toast from 'react-hot-toast';

interface NotificationPreferencesProps {
  onClose?: () => void;
}

// Resolve true → save preference ON. Resolve false → fire toast + save OFF.
//
//   User clicks Enable → slidedown closes, native browser prompt opens.
//     → Allow on native → optedIn flips → resolve true (save ON)
//     → Block on native → permission='denied' → resolve false
//   User clicks Not now → action='cancelled' → resolve false immediately
//   User dismisses via X / Esc / click outside (no button click) →
//     slidedown removed from DOM with action=null → resolve false
//   Native prompt ignored past timeout → resolve false
const waitForOptIn = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    if (oneSignalService.isOptedIn()) {
      resolve(true);
      return;
    }
    const startedAt = Date.now();
    let hasSeenSlidedown = false;
    let resolved = false;
    const finish = (result: boolean) => {
      if (resolved) return;
      resolved = true;
      clearInterval(interval);
      resolve(result);
    };
    const interval = setInterval(() => {
      const slidedownExists = !!document.getElementById('onesignal-slidedown-container');
      if (slidedownExists) hasSeenSlidedown = true;

      if (oneSignalService.isOptedIn()) {
        finish(true);
        return;
      }

      const action = oneSignalService.getSlidedownUserAction();

      // Not now click → fail fast, no waiting for native prompt.
      if (action === 'cancelled') {
        finish(false);
        return;
      }

      // Native browser prompt explicitly blocked.
      const browserPermission =
        'Notification' in window ? Notification.permission : 'default';
      if (browserPermission === 'denied') {
        finish(false);
        return;
      }

      // Slidedown gone via X / Esc / click outside (no button click).
      if (hasSeenSlidedown && !slidedownExists && action === null) {
        finish(false);
        return;
      }

      // action === 'enabled' → keep waiting for native prompt to resolve
      // (optedIn flip on Allow, or permission='denied' caught above).

      if (Date.now() - startedAt >= timeoutMs) {
        finish(false);
      }
    }, 200);
  });

export function NotificationPreferences({ onClose }: NotificationPreferencesProps) {
  const { preferences, fetchPreferences, updatePreferences } = useNotifications();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  // Reactive mirror of oneSignalService.isReady(). The service initializes
  // asynchronously from useNotifications; reading isReady() directly in JSX
  // captures the value at first render only, so cold browsers (SW not warm,
  // extensions slowing the SDK CDN, etc.) freeze on "Not configured for this
  // tenant" even after init eventually succeeds.
  const [isPushReady, setIsPushReady] = useState(() => oneSignalService.isReady());

  // Local state for form
  const [channels, setChannels] = useState<ChannelPreferences>({
    in_app: { enabled: true },
    push: { enabled: false },
    email: { enabled: true },
  });

  // Poll for OneSignal init completion. Gives up after ~15s — by then the
  // SDK has either initialized or genuinely failed (blocked by extension,
  // network, permission denied) and the disabled state reflects reality.
  useEffect(() => {
    if (isPushReady) return;
    const interval = setInterval(() => {
      if (oneSignalService.isReady()) {
        setIsPushReady(true);
      }
    }, 250);
    const timeout = setTimeout(() => clearInterval(interval), 15000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPushReady]);


  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      await fetchPreferences();
      setIsLoading(false);
    };
    loadPreferences();
  }, [fetchPreferences]);

  // Update local state when preferences load
  useEffect(() => {
    if (preferences) {
      setChannels(preferences.channels);
    }
  }, [preferences]);

  const handleChannelToggle = (channel: keyof ChannelPreferences) => {
    // All channels are pure local state flips here. The OneSignal subscribe
    // slidedown is deferred to Save — toggling on without saving should not
    // create a real OneSignal subscription that the DB doesn't reflect.
    setChannels((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        enabled: !prev[channel].enabled,
      },
    }));
  };

  const handleSave = async () => {
    // Detect push transition: was OFF in DB, now ON locally → user is opting in.
    // Only at this point do we ask the browser for push permission, so the user
    // can't end up subscribed in OneSignal but disabled in DB by toggling on
    // and walking away without saving.
    const wasEnabledInDb = preferences?.channels.push.enabled === true;
    const isEnablingPush = !wasEnabledInDb && channels.push.enabled === true;

    if (isEnablingPush) {
      if (!oneSignalService.isReady()) {
        toast.error('Push notifications are not available for this tenant');
        // Revert the local toggle so UI matches the DB.
        setChannels((prev) => ({ ...prev, push: { enabled: false } }));
        return;
      }

      // Browser permission already denied — the slidedown can render but
      // the native prompt won't fire (browser blocks it after a prior Block).
      // Only the user can recover by resetting site permission in their
      // browser settings, so we skip the slidedown and tell them how.
      // Don't touch the DB — preference stays in its previous state.
      if ('Notification' in window && Notification.permission === 'denied') {
        toast.error(
          'Notifications are blocked for this site. Reset notification permission in your browser settings, then try again.',
          { duration: 6000 }
        );
        setChannels((prev) => ({ ...prev, push: { enabled: false } }));
        return;
      }

      // Only show the slidedown when the user isn't already subscribed at the
      // OneSignal level (e.g. another device already granted permission for
      // this user). If they are, we just flip the DB flag.
      if (!oneSignalService.isOptedIn()) {
        setIsSubscribing(true);
        try {
          await oneSignalService.showSubscribePrompt();
          // Wait up to 30 s for the user to click Subscribe on the slidedown
          // + Allow on the native browser prompt.
          const optedIn = await waitForOptIn(30_000);
          if (!optedIn) {
            // Force-dismiss the slidedown if it's still in the DOM —
            // the user either ignored it past the timeout or clicked
            // outside without choosing. Don't let it linger past the toast.
            oneSignalService.hideSlidedown();
            // If they just clicked Block on the native prompt during this
            // attempt, point them at the browser-settings reset path.
            // Otherwise (Not now / X / Esc / timeout), use the generic copy.
            const justBlocked =
              'Notification' in window && Notification.permission === 'denied';
            if (justBlocked) {
              toast.error(
                'Notifications are blocked for this site. Reset notification permission in your browser settings, then try again.',
                { duration: 6000 }
              );
            } else {
              toast.error(
                'Push notifications not enabled. Please allow notifications when prompted.'
              );
            }
            // Revert the local UI toggle so it matches the DB state, but
            // don't write to the DB — preference stays in its previous state.
            setChannels((prev) => ({ ...prev, push: { enabled: false } }));
            return;
          }
        } finally {
          setIsSubscribing(false);
        }
      }
    }

    setIsSaving(true);
    const result = await updatePreferences({ channels });
    setIsSaving(false);

    if (result) {
      onClose?.();
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header skeleton */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <SkeletonLoader type="custom" className="w-10 h-10 rounded-xl" />
            <div className="space-y-2">
              <SkeletonLoader type="text" className="h-5 w-48" />
              <SkeletonLoader type="text" className="h-3 w-64" />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Channels skeleton */}
          <div className="space-y-4">
            <SkeletonLoader type="text" className="h-3 w-40" />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <SkeletonLoader type="custom" className="w-9 h-9 rounded-lg" />
                  <div className="space-y-2">
                    <SkeletonLoader type="text" className="h-4 w-36" />
                    <SkeletonLoader type="text" className="h-3 w-52" />
                  </div>
                </div>
                <SkeletonLoader type="custom" className="w-11 h-6 rounded-full" />
              </div>
            ))}
          </div>

          {/* Save button skeleton */}
          <SkeletonLoader type="custom" className="h-10 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-xl">
            <Bell className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Notification Preferences
            </h2>
            <p className="text-sm text-gray-500">
              Manage how you receive notifications
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Notification Channels */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
            Notification Channels
          </h3>

          <div className="space-y-4">
            {/* In-App */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Monitor className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">In-App Notifications</p>
                  <p className="text-sm text-gray-500">
                    Receive notifications within the application
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={channels.in_app.enabled}
                  onChange={() => handleChannelToggle('in_app')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {/* Push Notifications toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Smartphone className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Push Notifications</p>
                  <p className="text-sm text-gray-500">
                    {!isPushReady
                      ? 'Not configured for this tenant'
                      : 'Receive browser push notifications'}
                  </p>
                </div>
              </div>
              <label
                className={`relative inline-flex items-center ${
                  !isPushReady
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={channels.push.enabled}
                  onChange={() => handleChannelToggle('push')}
                  disabled={!isPushReady}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {/* Cross-browser subscription note — explains the manual re-toggle
                needed to subscribe a new browser when the saved preference is
                already ON. Each browser holds its own push subscription, so
                opening Settings on Browser B with the toggle already ON does
                NOT subscribe Browser B by itself — the user has to flip OFF
                + Save, then ON + Save to re-trigger the slidedown for the
                new browser. Shown only when the SDK is initialized (otherwise
                the note is irrelevant). */}
            {isPushReady && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <Bell className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-900 leading-relaxed">
                  <span className="font-medium">Using a new browser?</span> Each browser maintains its own push subscription.
                  If push notifications are already enabled here but the new browser isn't receiving them, turn the toggle <strong>off</strong> and save,
                  then turn it back <strong>on</strong> and save again in the new browser to subscribe.
                </p>
              </div>
            )}


            {/* Email */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Mail className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email Notifications</p>
                    <p className="text-sm text-gray-500">
                      Receive notifications via email
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={channels.email.enabled}
                    onChange={() => handleChannelToggle('email')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {/* Email Frequency removed from admin preferences — delivery mode
                  is now controlled per-notification in the Send Notification form.
                  This section should be on the LEARNER-side preferences page where
                  users control their own email delivery preference. */}
            </div>
          </div>
        </section>

      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-end gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || isSubscribing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSubscribing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Waiting for permission…
              </>
            ) : isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Preferences
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
}

export default NotificationPreferences;
